// The pulls contract: R&D, repair and scrap pulls, put-backs, the scrap bin, stock
// moves. Every shape here is read off api/src/inventory/pulls-router.js and
// stock-moves-router.js. Cost fields (`*_cents`) arrive only for can_financial users.
// Doc: web/docs/features/stock-pulls.md.

import { useApi } from '../../apiHelpers';

export type PullReason = 'rnd' | 'repair' | 'scrap';

export const REASON_LABELS: Record<PullReason | 'thrown_out', string> = {
  rnd: 'R&D',
  repair: 'Repair',
  scrap: 'Scrap',
  thrown_out: 'Thrown out',
};

export interface LocationRef {
  id: number;
  name: string;
  type: string;
  /** Set on a section (`PLLT-B3`); null or absent elsewhere. */
  code?: string | null;
  section_code?: string | null;
  area_name?: string | null;
  warehouse_name?: string | null;
  path?: string;
}

export interface BoxItem {
  receipt_item_id: string;
  part_catalog_id: number;
  sku: string | null;
  display_name: string | null;
  quantity_remaining: number;
  tracked_per_instance: boolean;
  location: LocationRef;
  pullable: boolean;
  movable: boolean;
  /** Why a pull would be refused here, as a sentence. Null when pullable. */
  refusal: string | null;
  /** Boards pull by QR only and this is a board box: a count pull may only scrap. */
  scrap_only?: boolean;
  /** What to tell the operator about scrap_only. */
  hint?: string | null;
}

/** The still-active section a box in Assembly was picked from; null when unknown or mixed. */
export interface CameFrom {
  id: number;
  code: string;
  path: string;
}

export interface ResolvedBox {
  kind: 'box';
  barcode: string;
  items: BoxItem[];
  /** Non-null only for a box in Assembly whose items all came from one active section. */
  came_from?: CameFrom | null;
}

export interface ResolvedPart {
  kind: 'board' | 'part_instance';
  board_id?: string;
  part_instance_id?: string;
  primary_uid?: string;
  board_type?: string;
  sku?: string | null;
  display_name?: string | null;
  serialnumber: string | null;
  receipt_item_id: string | null;
  location: LocationRef | null;
  /** Already charged to a drone or a pull. */
  charged: boolean;
  pullable: boolean;
  refusal: string | null;
}

export type Resolved = ResolvedBox | ResolvedPart;

export interface PullInput {
  receipt_item_id?: string;
  board_id?: string;
  part_instance_id?: string;
  quantity: number;
  reason: PullReason;
  project?: string;
  serialnumber?: string;
  note?: string;
}

export interface PullResult {
  transaction_id: string;
  reason: PullReason;
  quantity: number;
  item: { receipt_item_id: string; sku: string | null; display_name: string | null; quantity_remaining: number };
  from: LocationRef;
}

export interface ReportRow {
  project: string | null;
  reason: PullReason | 'thrown_out';
  pulled: number;
  put_back: number;
  net: number;
  cost_cents?: number | null;
}

export interface ScrapSummary {
  binned: number;
  thrown_out: number;
  in_bin: number;
  over_30_days: number;
  bin_value_cents?: number | null;
}

export interface Report {
  rows: ReportRow[];
  scrap: ScrapSummary;
}

export interface PullLine {
  transaction_id: string;
  occurred_at: string;
  reason: PullReason | 'thrown_out';
  project: string | null;
  serialnumber: string | null;
  quantity: number;
  put_back: number;
  /** quantity less put_back: the most a put-back may still return. */
  remaining: number;
  sku: string | null;
  display_name: string | null;
  barcode: string | null;
  from_location: string | null;
  performed_by_name: string | null;
  cost_cents?: number | null;
}

export interface ScrapBinItem {
  receipt_item_id: string;
  barcode: string;
  sku: string | null;
  display_name: string | null;
  quantity_remaining: number;
  binned_at: string;
  origin: 'receiving' | 'removal' | 'pull' | 'other';
  origin_note: string | null;
  unit_cost_cents?: number | null;
}

export interface ReportFilter {
  from?: string;
  to?: string;
  reason?: string;
  /** Exact project, case-insensitive. */
  project?: string;
  /** Project contains this text. */
  search?: string;
  /** '1': only pulls with no project. */
  no_project?: string;
}

/** Per-database switches (app_settings), admin-flipped on the Locations page. */
export interface PullSettings {
  /** A repair pull must come from the Repair or the scrap bin. */
  repairFromRepairOnly: boolean;
  /** A box of a flashed board kind pulls by the board's QR, never by count (scrap stays open). */
  boardsByQrOnly: boolean;
}

export interface MoveResult {
  moved: number;
  split: boolean;
  item: { receipt_item_id: string; quantity_remaining: number };
  to: LocationRef;
}

/** `POST /stock-moves` with a box barcode: the whole box, one transaction. */
export interface BoxMoveResult {
  moved: number;
  items: { receipt_item_id: string; barcode: string; sku: string | null; display_name: string | null; quantity: number }[];
  from: LocationRef;
  to: { id: number; name: string; type: string; code?: string | null; path?: string };
}

/** Where a whole box goes: a section code typed or scanned, or a section id from its own card. */
export type BoxMoveTarget = { to_location_code: string } | { to_location_id: number };

export interface MoveDestination extends LocationRef {
  parent_name: string | null;
}

const qs = (f: ReportFilter = {}) => {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export function usePullsApi() {
  const api = useApi();
  return {
    resolve: (code: string): Promise<Resolved> =>
      api.get(`/pulls/resolve?code=${encodeURIComponent(code.trim())}`, { silent: true }),

    pull: (input: PullInput): Promise<PullResult> => api.post('/pulls', { body: input, silent: true }),

    putBack: (transactionId: string, quantity: number): Promise<{ transaction_id: string; remaining_on_pull: number }> =>
      api.post(`/pulls/${encodeURIComponent(transactionId)}/put-back`, { body: { quantity }, silent: true }),

    projects: (): Promise<{ projects: { project: string; last_used: string }[] }> => api.get('/pulls/projects'),

    report: (f?: ReportFilter): Promise<Report> => api.get(`/pulls/report${qs(f)}`),

    /** Same filters as report; the browser downloads it. */
    reportCsvPath: (f?: ReportFilter): string => `/pulls/report.csv${qs(f)}`,

    lines: (f?: ReportFilter): Promise<{ lines: PullLine[] }> => api.get(`/pulls/lines${qs(f)}`),

    droneLines: (serial: string): Promise<{ lines: PullLine[] }> =>
      api.get(`/pulls/drone/${encodeURIComponent(serial)}`),

    scrapBin: (): Promise<{ items: ScrapBinItem[]; bin_value_cents?: number | null }> => api.get('/pulls/scrap-bin'),

    throwOut: (items: { receipt_item_id: string; quantity?: number }[]): Promise<{ thrown_out: number }> =>
      api.post('/pulls/thrown-out', { body: { items }, silent: true }),

    settings: (): Promise<PullSettings> => api.get('/pulls/settings'),

    saveSettings: (s: Partial<PullSettings>): Promise<PullSettings> =>
      api.put('/pulls/settings', { body: s }),

    move: (input: { receipt_item_id: string; quantity?: number; to_location_id: number }): Promise<MoveResult> =>
      api.post('/stock-moves', { body: input, silent: true }),

    moveBox: (barcode: string, to: BoxMoveTarget): Promise<BoxMoveResult> =>
      api.post('/stock-moves', { body: { barcode, ...to }, silent: true }),

    destinations: (): Promise<{ locations: MoveDestination[] }> => api.get('/stock-moves/destinations'),
  };
}
